import { createAction, props } from '@ngrx/store';
import { ExtensaAuthState } from '../connectivity/types';
import { MainToolbarHeaderInfo } from '../se-toolbar/se-header-toolbar/se-header-info/se-header-info.component';
import { KeyImage, XmlRequestType } from '../services/imaging/common';
import {
    ModelStructuredReport,
    ModelUSMeasure,
    ModelUSMeasureReportInfo,
    ModelUSStressEchoInit,
    ModelUSStressEchoSelect,
    PacsTuple,
} from '../services/imaging/ebit-viewer-api';
import { AssociationType, EcgNotification } from '../models/signalr-notifications.models';
import { ImagingOpenParams } from 'estensa-module';

export type KeyImagePacsTuplePair = { image: KeyImage; pacsTuple: PacsTuple };

export const seChangeTheme = createAction('[Se] ChangeTheme', props<{ theme: 'dark' | 'light' }>());

export const seEcgAlarm = createAction('[Se] EcgAlarm', props<{ alarm: EcgNotification; correlationId: string }>());

export const seEcgAlarmWarning = createAction(
    '[Se] EcgAlarmWarning',
    props<{ alarm: EcgNotification; correlationId: string }>(),
);

export const seExaminationAssociation = createAction(
    '[Se] ExaminationAssociation',
    props<{ idExamination: string; associationType: AssociationType }>(),
);

export const seExaminationStore = createAction('[Se] ExaminationStore', props<{ idExamination: string }>());

export const seExtensaAuth = createAction('[Se] ExtensaAuth', props<ExtensaAuthState>());

export const seExtensaConnected = createAction('[Se] ExtensaConnected', props<{ isConnected: boolean }>());

export const seHeaderClick = createAction('[Se] HeaderClick');

export const seIsIntegrationAppConnected = createAction(
    '[Se] IsIntegrationConnected',
    props<{ isConnected: boolean }>(),
);

export const seLogin = createAction('[Se] Login', props<{ sso?: 'WEB' | 'WPF' }>());

export const seLogoClick = createAction('[Se] LogoClick');

export const seNotificationError = createAction('[Se] NotificationError', props<{ message: string }>());

export const seNotificationSuccess = createAction('[Se] NotificationSuccess', props<{ message: string }>());

export const seNotificationWarning = createAction('[Se] NotificationWarning', props<{ message: string }>());

export const seShowClientInfo = createAction('[Se] ShowClientInfo');

export const seShowHelp = createAction('[Se] ShowHelp');

export const seShowViewerInfo = createAction('[Se] ShowViewerInfo');

export const seSignalrConnected = createAction('[Se] SignalrConnected', props<{ isConnected: boolean }>());

export const seViewerConnected = createAction('[Se] ViewerConnected', props<{ isConnected: boolean }>());

export const seSetToolbarInfo = createAction(
    '[Se] SetToolbarInfo',
    props<{ info1?: MainToolbarHeaderInfo; info2?: MainToolbarHeaderInfo }>(),
);

export const seSetToolbarDescription1 = createAction('[Se] SetToolbarDescription1', props<{ description1: string }>());

export const seUserLogout = createAction('[Se] UserLogout', props<{ forced?: boolean }>());

export const seViewerKeyImageAdd = createAction('[Se] ViewerKeyImageAdd', props<KeyImagePacsTuplePair>());

export const seViewerKeyImagesDelete = createAction(
    '[Se] ViewerKeyImageDelete',
    props<{ images: KeyImagePacsTuplePair[] }>(),
);

export const seViewerKeyImageRefresh = createAction('[Se] ViewerKeyImageRefresh', props<KeyImagePacsTuplePair>());

export const seViewerKeyImagesSet = createAction(
    '[Se] ViewerKeyImagesSet',
    props<{ images: KeyImagePacsTuplePair[] }>(),
);

export const seViewerKeyImagesPerRows = createAction('[Se] ViewerKeyImagesPerRows', props<{ rows: number }>());

export const seViewerUSMeasureDeleteRequest = createAction(
    '[Se] ViewerUSMeasureDeleteRequest',
    props<{
        measure: ModelUSMeasure;
        callback: string;
        requestId: string;
    }>(),
);

export const seViewerUSMeasureDeleteResponse = createAction(
    '[Se] ViewerUSMeasureDeleteResponse',
    props<{
        result: boolean;
        callback: string;
        requestId: string;
    }>(),
);

export const seViewerUSMeasureGetReportRequest = createAction(
    '[Se] ViewerUSMeasureGetReportRequest',
    props<{
        pacsTuple: PacsTuple;
        callback: string;
        requestId: string;
    }>(),
);

export const seViewerUSMeasureGetReportResponse = createAction(
    '[Se] ViewerUSMeasureGetReportResponse',
    props<{
        report: ModelUSMeasureReportInfo;
        callback: string;
        requestId: string;
    }>(),
);

export const seViewerUSMeasureImportJsonRequest = createAction(
    '[Se] ViewerUSMeasureImportJsonRequest',
    props<{ repositoryName: string; nPla: PacsTuple }>(),
);

export const seViewerUSMeasureImportXmlRequest = createAction(
    '[Se] ViewerUSMeasureImportXmlRequest',
    props<{ examGuid: string; nPla: PacsTuple }>(),
);

export const seViewerUSStressEchoInit = createAction('[Se] ViewerUSStressEchoInit', props<ModelUSStressEchoInit>());

export const seViewerUSStressEchoSelect = createAction(
    '[Se] ViewerUSStressEchoSelect',
    props<ModelUSStressEchoSelect>(),
);

export const seViewerSRAdd = createAction('[Se] ViewerSRAdd', props<ModelStructuredReport>());

export const seViewerSRDelete = createAction('[Se] ViewerSRDelete', props<ModelStructuredReport>());

export const seViewerUSMeasureImportRequestThirdParts = createAction(
    '[Se] ViewerXmlImport',
    props<{ requestType: XmlRequestType; xml: string }>(),
);

export const seViewerUSKineticMeasureImportRequest = createAction(
    '[Se] ViewerKineticXmlImport',
    props<{ requestType: XmlRequestType; xml: string }>(),
);

export const seLockedExams = createAction('[Se] LockedExams', props<{ examsToClose: string[] }>());

/** US2AI actions */
export const seUS2AIAlert = createAction(
    '[Se] seUS2AIAlert',
    props<{ idRepository: string; studyInstanceUid: string }>(),
);

/** --------- */

/** 110807 111921 openViewer Action
 * action gestita in root.effects.ts che si preoccupa di capire in quale wl/cp si trova e passa l'informazione
 * ad imaging-service che apre a sua volta il viewer corretto in base ai settings
 * il tipo delle props data l'emergenza e` stato lasciato identico a come definito in IBrokerCheckServiceNg1
 * quindi con i due any. @TODO da rivedere! */
export const seOpenImagesByEprEvent = createAction(
    '[Se] openImagesByEprEvent',
    props<{
        eprEvent: any;
        credentials: any;
        openParams: ImagingOpenParams;
    }>(),
);

export const seOpenViewer = createAction('[Se] openViewer');
/** ---------- */
